import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';
import {
  ITeacherAssignment,
  ITeacherPaginatedAssignments,
} from '../../../../config/api/models/Assignment';
import {
  Divider,
  AccordionDetails,
  AccordionSummary,
  Typography,
  styled,
  Theme,
} from '@material-ui/core';
import { Accordion, Message, GetMoreAssignmentsButton, Loading } from '../../../shared/components';
import { AssignmentItem } from './AssignmentItem';
import AssignmentFollowUp from './AssignmentFollowUp';
import { useFetch, useUser } from '../../../shared/hooks';
import apiRoutes from '../../../../config/api/routes';
import { useTranslation } from 'react-i18next';
import { LogActionTypes, useDataLogging } from '../../../shared/data-logging';

type Props = {
  bookFilter?: string;
  groupFilter?: string;
};

const Wrapper = styled('div')<Theme>(({ theme }) => ({
  paddingBottom: theme.spacing(6),
}));

const StyledAccordionSummary = styled(AccordionSummary)<Theme>(({ theme }) => ({
  display: 'flex',
  width: '100%',
  padding: theme.spacing(2, 4),
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(1, 0),
  },
}));

const ContentWrapper = styled(Typography)<Theme>(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(6, 8),
}));

const AssignmentsList: FC<Props> = ({ bookFilter, groupFilter }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [lastPageLoaded, setLastPageLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [bookId, setBookId] = useState(bookFilter);
  const [groupId, setGroupId] = useState(groupFilter);
  const log = useDataLogging();
  const [assignments, patchAssignments] = useReducer(
    (state: any, action: any) => {
      switch (action.type) {
        case 'add':
          return { list: state.list.concat(action.payload) };
        case 'reset':
          return { list: [] };
      }
      return { list: [] };
    },
    { list: [] }
  );
  const [assignmentsResponse, getAssignments] = useFetch<ITeacherPaginatedAssignments>('');

  useEffect(() => {
    patchAssignments({ type: 'reset' });
    setBookId(bookFilter);
    setGroupId(groupFilter);
    setPage(1);

    log(LogActionTypes.USER_SEARCH_TASK, {
      bookId: bookFilter || '',
      groupId: groupFilter || '',
    });
  }, [bookFilter, groupFilter, log]);

  useEffect(() => {
    if (assignmentsResponse.success) {
      patchAssignments({
        type: 'add',
        payload: assignmentsResponse.data.data,
      });

      setLastPageLoaded(
        assignmentsResponse.data.pagination.currentPage ===
          assignmentsResponse.data.pagination.totalPages
      );
    }
  }, [assignmentsResponse, patchAssignments]);

  useEffect(() => {
    patchAssignments({ type: 'reset' });
  }, [patchAssignments]);

  useEffect(() => {
    if (page > 0) {
      getAssignments({
        path: apiRoutes.assignments({
          userId: user.id,
          page,
          bookFilter: bookId,
          groupFilter: groupId,
        }),
      });
    }
  }, [user, page, bookId, groupId, getAssignments]);

  const getMoreAssignments = useCallback(() => {
    if (
      assignmentsResponse.success &&
      assignmentsResponse.data.pagination.totalPages >
        assignmentsResponse.data.pagination.currentPage
    ) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [assignmentsResponse]);

  const handleDeleteAssignment = useCallback(() => {
    patchAssignments({ type: 'reset' });
    setPage(0);
    setTimeout(() => {
      setPage(1);
    }, 100);
  }, []);

  const handleChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Wrapper>
      {assignmentsResponse.pending && <Loading />}
      {assignmentsResponse.error && <Message type="error">{assignmentsResponse.reason}</Message>}
      {assignmentsResponse.success && assignments.list.length === 0 && (
        <ContentWrapper variant="body1">{t('shared.noResults')}</ContentWrapper>
      )}
      {assignments.list.map((assignment: ITeacherAssignment, index: number) => (
        <Accordion
          key={assignment.id}
          expanded={expanded === `assignment_${index + 1}`}
          onChange={handleChange(`assignment_${index + 1}`)}
        >
          <StyledAccordionSummary>
            <AssignmentItem assignment={assignment} onRemove={handleDeleteAssignment} />
          </StyledAccordionSummary>
          <AccordionDetails>
            <AssignmentFollowUp
              expanded={expanded === `assignment_${index + 1}`}
              assignment={assignment}
            />
          </AccordionDetails>
          {index !== assignments.list.length - 1 && <Divider />}
        </Accordion>
      ))}
      {assignments.list.length > 0 && !lastPageLoaded && (
        <GetMoreAssignmentsButton
          label={t('studentDashboard.assignments.loadMore')}
          loading={assignmentsResponse.pending}
          onClick={getMoreAssignments}
        />
      )}
    </Wrapper>
  );
};

export default AssignmentsList;
